import { useEffect, useMemo, useRef, useState, MouseEvent as ReactMouseEvent } from 'react';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { MdContentCopy, MdCheck, MdPlayArrow, MdArrowUpward } from 'react-icons/md';

// Same convention as formTemplates/edit.tsx's DISPLAY_API_URL: show the public
// api.leadrs.co domain in docs/examples instead of the raw Scalingo hostname,
// while the actual "Envoyer la requête" call below still targets CONFIG.apiUrl
// (works in every environment, including local dev).
const DISPLAY_API_URL =
    CONFIG.apiUrl.replace(/\/$/, '') === 'https://api-leadrs-co.osc-fr1.scalingo.io'
        ? 'https://api.leadrs.co'
        : CONFIG.apiUrl.replace(/\/$/, '');

export interface ParamDef {
    name: string;
    label?: string;
    placeholder?: string;
    defaultValue?: string;
    required?: boolean;
    // When set, the field renders as a searchable dropdown over these values
    // instead of a plain text input. A function form can depend on the other
    // query fields' current values (e.g. subIndustry options narrowed by the
    // currently chosen industry).
    options?: string[] | ((queryValues: Record<string, string>) => string[]);
    // Shown in the dropdown instead of the default "Aucun résultat" when there are
    // no options to pick from yet (e.g. no industry chosen).
    emptyMessage?: string;
    // Small note + in-page link shown under the field, e.g. pointing back at the
    // endpoint block that these options come from.
    sourceHint?: { text: string; anchorId: string };
}

interface ApiEndpointPlaygroundProps {
    id?: string;
    method: 'GET' | 'POST';
    title: string;
    description: string;
    pathTemplate: string; // e.g. '/marketplace/leads/:leadId/buy'
    pathParams?: ParamDef[];
    queryParams?: ParamDef[];
    // For an endpoint whose payload is a JSON body rather than query/path params (e.g. POST
    // /lead/api/submit) — renders as its own input section, sent as the request body (with
    // Content-Type: application/json) in curl/fetch snippets and the live "Envoyer" call.
    bodyFields?: ParamDef[];
    requiredScope: string;
    apiKey: string;
}

// Scrolls to another block on the same page and briefly highlights it, so a
// "these values come from X" link actually lands somewhere findable.
const scrollToAnchor = (anchorId: string) => (e: ReactMouseEvent) => {
    e.preventDefault();
    const el = document.getElementById(anchorId);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('ring-2', 'ring-leadrs-brand');
    setTimeout(() => el.classList.remove('ring-2', 'ring-leadrs-brand'), 1500);
};

const SearchableSelect = ({
    value,
    onChange,
    options,
    placeholder,
    emptyMessage,
}: {
    value: string;
    onChange: (value: string) => void;
    options: string[];
    placeholder?: string;
    emptyMessage?: string;
}) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filtered = useMemo(() => {
        const needle = query.trim().toLowerCase();
        if (!needle) return options;
        return options.filter((o) => o.toLowerCase().includes(needle));
    }, [options, query]);

    return (
        <div ref={containerRef} className="relative">
            <input
                type="text"
                value={open ? query : value}
                onChange={(e) => {
                    setQuery(e.target.value);
                    if (!open) setOpen(true);
                }}
                onFocus={() => {
                    setQuery('');
                    setOpen(true);
                }}
                placeholder={placeholder}
                className="w-full rounded-md border border-leadrs-stroke px-2.5 py-1.5 font-mono text-xs text-leadrs-text"
            />
            {value && !open && (
                <button
                    type="button"
                    onClick={() => onChange('')}
                    aria-label="Effacer"
                    className="absolute right-2 top-1/2 -translate-y-1/2 font-geist text-xs text-leadrs-text-soft hover:text-leadrs-text"
                >
                    ✕
                </button>
            )}
            {open && (
                <div className="absolute z-10 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-leadrs-stroke bg-white shadow-lg">
                    {filtered.length === 0 ? (
                        <div className="px-3 py-2 font-geist text-xs text-leadrs-text-soft">{emptyMessage || 'Aucun résultat'}</div>
                    ) : (
                        filtered.map((opt) => (
                            <button
                                type="button"
                                key={opt}
                                onClick={() => {
                                    onChange(opt);
                                    setOpen(false);
                                    setQuery('');
                                }}
                                className={`block w-full px-3 py-1.5 text-left font-geist text-xs hover:bg-leadrs-bg ${
                                    opt === value ? 'bg-leadrs-purple-soft text-leadrs-brand' : 'text-leadrs-text'
                                }`}
                            >
                                {opt}
                            </button>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

// Exported for reuse by bespoke testers that don't fit this component's generic
// path/query/body-param shape (e.g. seller/apiDocs/SubmitLeadTester.tsx, whose fields depend
// dynamically on a value the user types into the tester itself).
export const CodeBlock = ({ code }: { code: string }) => {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        navigator.clipboard.writeText(code).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };
    return (
        <div className="relative">
            <pre className="overflow-x-auto rounded-lg bg-leadrs-text px-4 py-3 font-mono text-xs leading-relaxed text-white">
                {code}
            </pre>
            <button
                onClick={handleCopy}
                className="absolute right-2 top-2 flex items-center gap-1 rounded-md bg-white/10 px-2 py-1 text-xs text-white hover:bg-white/20"
            >
                {copied ? <MdCheck className="h-3.5 w-3.5" /> : <MdContentCopy className="h-3.5 w-3.5" />}
                {copied ? 'Copié' : 'Copier'}
            </button>
        </div>
    );
};

export default function ApiEndpointPlayground({
    id,
    method,
    title,
    description,
    pathTemplate,
    pathParams = [],
    queryParams = [],
    bodyFields = [],
    requiredScope,
    apiKey,
}: ApiEndpointPlaygroundProps) {
    const [pathValues, setPathValues] = useState<Record<string, string>>(() =>
        Object.fromEntries(pathParams.map((p) => [p.name, p.defaultValue || '']))
    );
    const [queryValues, setQueryValues] = useState<Record<string, string>>(() =>
        Object.fromEntries(queryParams.map((p) => [p.name, p.defaultValue || '']))
    );
    const [bodyValues, setBodyValues] = useState<Record<string, string>>(() =>
        Object.fromEntries(bodyFields.map((p) => [p.name, p.defaultValue || '']))
    );
    const [sending, setSending] = useState(false);
    const [response, setResponse] = useState<{ status: number | null; data: any } | null>(null);

    const missingRequired = [...pathParams, ...bodyFields].filter(
        (p) => p.required && !(pathValues[p.name] ?? bodyValues[p.name])?.trim()
    );

    const resolvedPath = useMemo(() => {
        let path = pathTemplate;
        pathParams.forEach((p) => {
            const value = pathValues[p.name]?.trim();
            path = path.replace(`:${p.name}`, value ? encodeURIComponent(value) : `:${p.name}`);
        });
        return path;
    }, [pathTemplate, pathParams, pathValues]);

    // A dependent field (e.g. subIndustry) can go stale when the field it depends on
    // (e.g. industry) changes — clear it rather than silently sending a mismatched pair.
    useEffect(() => {
        queryParams.forEach((p) => {
            if (typeof p.options !== 'function') return;
            const current = queryValues[p.name];
            if (current && !p.options(queryValues).includes(current)) {
                setQueryValues((prev) => ({ ...prev, [p.name]: '' }));
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryValues]);

    const queryString = useMemo(() => {
        const parts = queryParams
            .map((p) => [p.name, queryValues[p.name]?.trim()])
            .filter(([, v]) => !!v)
            .map(([n, v]) => `${encodeURIComponent(n as string)}=${encodeURIComponent(v as string)}`);
        return parts.length ? `?${parts.join('&')}` : '';
    }, [queryParams, queryValues]);

    const fullUrl = `${DISPLAY_API_URL}${resolvedPath}${queryString}`;
    const requestUrl = `${CONFIG.apiUrl}${resolvedPath}${queryString}`;
    const displayKey = apiKey.trim() || '<votre-cle-api>';

    // Body fields with a numeric-looking value are sent as numbers (e.g. subIndustryId), not
    // strings — everything else (the field inputs are free text) goes through as-is.
    const requestBody = useMemo(() => {
        if (bodyFields.length === 0) return null;
        const body: Record<string, string | number> = {};
        bodyFields.forEach((p) => {
            const raw = bodyValues[p.name]?.trim();
            if (!raw) return;
            body[p.name] = /^-?\d+(\.\d+)?$/.test(raw) ? Number(raw) : raw;
        });
        return body;
    }, [bodyFields, bodyValues]);

    const headerLines = requestBody
        ? `-H "x-api-key: ${displayKey}" \\\n  -H "Content-Type: application/json"`
        : `-H "x-api-key: ${displayKey}"`;
    const curlSnippet = requestBody
        ? `curl -X ${method} "${fullUrl}" \\\n  ${headerLines} \\\n  -d '${JSON.stringify(requestBody)}'`
        : `curl -X ${method} "${fullUrl}" \\\n  ${headerLines}`;

    const fetchHeaders = requestBody
        ? `{\n    "x-api-key": "${displayKey}",\n    "Content-Type": "application/json"\n  }`
        : `{\n    "x-api-key": "${displayKey}"\n  }`;
    const fetchSnippet = requestBody
        ? `fetch("${fullUrl}", {\n  method: "${method}",\n  headers: ${fetchHeaders},\n  body: JSON.stringify(${JSON.stringify(requestBody)})\n})\n  .then((res) => res.json())\n  .then((data) => console.log(data));`
        : `fetch("${fullUrl}", {\n  method: "${method}",\n  headers: ${fetchHeaders}\n})\n  .then((res) => res.json())\n  .then((data) => console.log(data));`;

    const handleSend = async () => {
        setSending(true);
        setResponse(null);
        try {
            const res = await axios.request({
                method,
                url: requestUrl,
                headers: requestBody
                    ? { 'x-api-key': apiKey.trim(), 'Content-Type': 'application/json' }
                    : { 'x-api-key': apiKey.trim() },
                ...(requestBody ? { data: requestBody } : {}),
            });
            setResponse({ status: res.status, data: res.data });
        } catch (e: any) {
            setResponse({ status: e?.response?.status ?? null, data: e?.response?.data ?? { message: e?.message || 'Erreur réseau' } });
        } finally {
            setSending(false);
        }
    };

    return (
        <div id={id} className="rounded-xl border border-leadrs-stroke bg-white px-5 py-5 scroll-mt-4 transition-shadow">
            <div className="flex flex-wrap items-center gap-2">
                <span
                    className={`rounded-full px-2 py-0.5 font-mono text-xs font-bold ${
                        method === 'GET' ? 'bg-leadrs-green-soft text-leadrs-green-focus' : 'bg-leadrs-yellow-soft text-leadrs-yellow-focus'
                    }`}
                >
                    {method}
                </span>
                <code className="font-mono text-sm text-leadrs-text">{pathTemplate}</code>
            </div>
            <h4 className="mt-2 font-geist text-base font-semibold text-leadrs-text">{title}</h4>
            <p className="mt-1 font-geist text-sm text-leadrs-text-soft">{description}</p>
            <p className="mt-1 font-geist text-xs text-leadrs-text-soft">
                Scope requis : <code className="font-mono text-leadrs-brand">{requiredScope}</code>
            </p>

            {(pathParams.length > 0 || queryParams.length > 0 || bodyFields.length > 0) && (
                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {bodyFields.map((p) => (
                        <div key={p.name}>
                            <label className="font-geist text-xs font-medium text-leadrs-text-soft">
                                {p.label || p.name} {p.required && <span className="text-leadrs-red-focus">*</span>}
                            </label>
                            <input
                                type="text"
                                value={bodyValues[p.name] || ''}
                                onChange={(e) => setBodyValues((prev) => ({ ...prev, [p.name]: e.target.value }))}
                                placeholder={p.placeholder}
                                className="mt-1 w-full rounded-md border border-leadrs-stroke px-2.5 py-1.5 font-mono text-xs text-leadrs-text"
                            />
                        </div>
                    ))}
                    {pathParams.map((p) => (
                        <div key={p.name}>
                            <label className="font-geist text-xs font-medium text-leadrs-text-soft">
                                {p.label || p.name} {p.required && <span className="text-leadrs-red-focus">*</span>}
                            </label>
                            <input
                                type="text"
                                value={pathValues[p.name] || ''}
                                onChange={(e) => setPathValues((prev) => ({ ...prev, [p.name]: e.target.value }))}
                                placeholder={p.placeholder}
                                className="mt-1 w-full rounded-md border border-leadrs-stroke px-2.5 py-1.5 font-mono text-xs text-leadrs-text"
                            />
                        </div>
                    ))}
                    {queryParams.map((p) => (
                        <div key={p.name}>
                            <label className="font-geist text-xs font-medium text-leadrs-text-soft">{p.label || p.name}</label>
                            {p.options ? (
                                <div className="mt-1">
                                    <SearchableSelect
                                        value={queryValues[p.name] || ''}
                                        onChange={(v) => setQueryValues((prev) => ({ ...prev, [p.name]: v }))}
                                        options={typeof p.options === 'function' ? p.options(queryValues) : p.options}
                                        placeholder={p.placeholder}
                                        emptyMessage={p.emptyMessage}
                                    />
                                </div>
                            ) : (
                                <input
                                    type="text"
                                    value={queryValues[p.name] || ''}
                                    onChange={(e) => setQueryValues((prev) => ({ ...prev, [p.name]: e.target.value }))}
                                    placeholder={p.placeholder}
                                    className="mt-1 w-full rounded-md border border-leadrs-stroke px-2.5 py-1.5 font-mono text-xs text-leadrs-text"
                                />
                            )}
                            {p.sourceHint && (
                                <a
                                    href={`#${p.sourceHint.anchorId}`}
                                    onClick={scrollToAnchor(p.sourceHint.anchorId)}
                                    className="mt-1 flex items-center gap-1 font-geist text-[11px] text-leadrs-text-soft hover:text-leadrs-brand"
                                >
                                    <MdArrowUpward className="h-3 w-3" />
                                    {p.sourceHint.text}
                                </a>
                            )}
                        </div>
                    ))}
                </div>
            )}

            <div className="mt-4">
                <p className="font-geist text-xs font-semibold uppercase tracking-wide text-leadrs-text-soft">URL</p>
                <code className="mt-1 block overflow-x-auto whitespace-nowrap rounded-md bg-leadrs-bg px-3 py-2 font-mono text-xs text-leadrs-text">
                    {fullUrl}
                </code>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
                <div>
                    <p className="mb-1 font-geist text-xs font-semibold uppercase tracking-wide text-leadrs-text-soft">curl</p>
                    <CodeBlock code={curlSnippet} />
                </div>
                <div>
                    <p className="mb-1 font-geist text-xs font-semibold uppercase tracking-wide text-leadrs-text-soft">fetch</p>
                    <CodeBlock code={fetchSnippet} />
                </div>
            </div>

            <div className="mt-4 border-t border-leadrs-stroke pt-4">
                <div className="flex items-center justify-between">
                    <p className="font-geist text-xs font-semibold uppercase tracking-wide text-leadrs-text-soft">Tester en direct</p>
                    <button
                        onClick={handleSend}
                        disabled={sending || !apiKey.trim() || missingRequired.length > 0}
                        className="linear flex items-center gap-1.5 rounded-lg bg-leadrs-brand px-3 py-1.5 font-geist text-xs font-medium text-white transition duration-200 hover:cursor-pointer disabled:opacity-50"
                    >
                        <MdPlayArrow className="h-4 w-4" />
                        {sending ? 'Envoi...' : 'Envoyer la requête'}
                    </button>
                </div>
                {!apiKey.trim() && (
                    <p className="mt-2 font-geist text-xs text-leadrs-text-soft">
                        Renseignez votre clé API en haut de page pour tester cette requête.
                    </p>
                )}
                {missingRequired.length > 0 && apiKey.trim() && (
                    <p className="mt-2 font-geist text-xs text-leadrs-red-focus">
                        Champ requis manquant : {missingRequired.map((p) => p.label || p.name).join(', ')}
                    </p>
                )}
                {response && (
                    <div className="mt-3">
                        <span
                            className={`rounded-full px-2 py-0.5 font-mono text-xs font-bold ${
                                response.status && response.status < 400
                                    ? 'bg-leadrs-green-soft text-leadrs-green-focus'
                                    : 'bg-leadrs-red-soft text-leadrs-red-focus'
                            }`}
                        >
                            {response.status ?? 'Erreur réseau'}
                        </span>
                        <pre className="mt-2 max-h-64 overflow-auto rounded-lg bg-leadrs-bg px-3 py-2 font-mono text-xs text-leadrs-text">
                            {JSON.stringify(response.data, null, 2)}
                        </pre>
                    </div>
                )}
            </div>
        </div>
    );
}
